import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { AdminFormPage, useAdminFormPersistence } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetProjectByIdQuery } from "@generated";
import { projectChromeQueryArgs } from "@components/SowHeader/projectChromeQuery";
import useChangeOrderAccess from "../hooks/useChangeOrderAccess";
import { useChangeOrder } from "../api";
import { causeLabel } from "../constants/causes";
import { statusText } from "../List/columns";
import SummaryBar from "../builder/SummaryBar";
import { DASH, formatHours, formatMoney } from "../builder/format";
import "../builder/builder.css";
import useAccountHasEntitlement from "@common/hooks/useAccountHasEntitlement";
import useChildScope from "./useChildScope";
import ShowSection from "./ShowSection";
import ScopeChangesGrid from "./ScopeChangesGrid";
import ChildGovernanceTable from "./ChildGovernanceTable";
import ChildExpensesTable from "./ChildExpensesTable";
import ChildProductsTable from "./ChildProductsTable";

interface Props {
  projectId: string;
}

// Read-only summary of a change request inside its parent project: its reason,
// totals and (scope mode) everything on the change request project, each part
// linking to where it's edited there.
const Show = ({ projectId }: Props) => {
  const navigate = useNavigate();
  const { changeOrderId = "" } = useParams();
  const access = useChangeOrderAccess();
  const { changeOrder, isLoading, error } = useChangeOrder(changeOrderId);
  const { toast, setToast } = useAdminFormPersistence();
  const { data: projectData } = useV2GetProjectByIdQuery(projectChromeQueryArgs(projectId));
  const { hasEntitlement } = useAccountHasEntitlement();
  const showProducts = hasEntitlement("product-quoting");
  const scope = useChildScope(
    changeOrder && !changeOrder.referenceOnly ? changeOrder.childProjectId : null,
    { products: showProducts }
  );
  const attributes: any = projectData?.data?.attributes;
  const currency = attributes?.["field-labels"]?.currency_unit ?? "$";

  if (isLoading) return <ScopeStackSpinner />;
  if (!changeOrder) {
    return (
      <div className="p-3">
        {error ? "This change request could not be loaded." : "Change request not found."}
      </div>
    );
  }
  if (changeOrder.parentProjectId !== projectId) return <Navigate to=".." replace />;

  const { label, referenceOnly, childProjectId } = changeOrder;
  const original = attributes
    ? {
        hours: Number(attributes["total-effort"]) || 0,
        revenue: Number(attributes["contract-revenue"]) || 0,
        cost: Number(attributes["contract-cost"]) || 0,
      }
    : null;
  const change = {
    hours: changeOrder.hours ?? 0,
    revenue: changeOrder.revenue ?? 0,
    cost: 0,
  };

  const scopeSections = (childId: string) => {
    if (scope.isLoading) return <ScopeStackSpinner />;
    if (scope.notFound) {
      return <p className="text-muted">This change request&apos;s project is no longer available.</p>;
    }
    if (scope.error) {
      return <p className="text-danger">Couldn&apos;t load this change request&apos;s scope.</p>;
    }
    const base = `/projects/${childId}`;
    return (
      <>
        <ShowSection
          title="Services"
          editLabel="Edit Services"
          href={`${base}/project_editor`}
          empty={scope.professional.length === 0 && scope.managed.length === 0}
        >
          <ScopeChangesGrid title="Professional Services" services={scope.professional} currency={currency} />
          <ScopeChangesGrid title="Managed Services" services={scope.managed} currency={currency} />
        </ShowSection>
        <ShowSection
          title="Project governance"
          editLabel="Edit Governance"
          href={`${base}/project_governances`}
          empty={scope.governances.length === 0}
        >
          <ChildGovernanceTable governances={scope.governances} currency={currency} />
        </ShowSection>
        <ShowSection
          title="Travel/Expense"
          editLabel="Edit Travel/Expense"
          href={`${base}/project_expenses`}
          empty={scope.expenses.length === 0}
          error={scope.expensesError ? "Couldn't load travel/expense." : null}
        >
          <ChildExpensesTable expenses={scope.expenses} currency={currency} />
        </ShowSection>
        {showProducts && (
          <ShowSection
            title="Product"
            editLabel="Edit Products"
            href={`${base}/project_materials`}
            empty={scope.products.length === 0}
            error={scope.productsError ? "Couldn't load products." : null}
          >
            <ChildProductsTable products={scope.products} currency={currency} />
          </ShowSection>
        )}
      </>
    );
  };

  return (
    <>
      <AdminFormPage
        title={
          <>
            {label}{" "}
            <span className="badge badge-secondary ml-2">
              {statusText(changeOrder, attributes?.["field-labels"])}
            </span>
          </>
        }
        extraActions={
          <>
            {access.canEdit(changeOrder) && (
              <Button className="btnSeawhite" onClick={() => navigate("edit")}>
                Edit Details
              </Button>
            )}
            {!referenceOnly && childProjectId && (
              <a className="btn squareGreenButton" href={`/projects/${childProjectId}/project_editor`}>
                Open in Change Request Project
              </a>
            )}
          </>
        }
      >
        <div className="changeOrderBuilder">
          <SummaryBar original={original} change={change} currency={currency} showMargin={false} />

          <h6 className="changeOrderSectionTitle">Reason for Change</h6>
          <dl className="row mb-4">
            <dt className="col-sm-3">Cause</dt>
            <dd className="col-sm-9">{causeLabel(changeOrder.cause)}</dd>
            <dt className="col-sm-3">Note</dt>
            <dd className="col-sm-9">{changeOrder.causeNote || DASH}</dd>
            <dt className="col-sm-3">External reference</dt>
            <dd className="col-sm-9">{changeOrder.externalReference || DASH}</dd>
            <dt className="col-sm-3">External URL</dt>
            <dd className="col-sm-9">
              {changeOrder.externalUrl ? (
                <a href={changeOrder.externalUrl} target="_blank" rel="noopener noreferrer">
                  {changeOrder.externalUrl}
                </a>
              ) : (
                DASH
              )}
            </dd>
          </dl>

          {referenceOnly ? (
            <>
              <h6>Tracked in another system</h6>
              <dl className="row">
                <dt className="col-sm-3">Hours</dt>
                <dd className="col-sm-9">{changeOrder.hours == null ? DASH : formatHours(changeOrder.hours)}</dd>
                <dt className="col-sm-3">Revenue</dt>
                <dd className="col-sm-9">
                  {changeOrder.revenue == null ? DASH : formatMoney(changeOrder.revenue, currency)}
                </dd>
              </dl>
            </>
          ) : childProjectId ? (
            scopeSections(childProjectId)
          ) : (
            <p className="text-muted">This change request&apos;s project is no longer available.</p>
          )}
        </div>
      </AdminFormPage>
      {toast && <ToastAlert variant={toast.variant} text={toast.text} onClose={() => setToast(null)} />}
    </>
  );
};

export default Show;
